import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { Eye, Pencil, Plus, SearchIcon } from "lucide-react";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  defaultPaginationOptions,
  endpoints,
  getProductStatusClassName,
  IProduct,
  IProductVariant,
  priceFormat,
  ProductStatus,
  SEARCH_DEBOUNCE_DELAY,
} from "muminsmart-utils";
import { useSession } from "@/contexts/AuthContext";
import {
  useUpdateProductMutation,
  useUpdateProductVariantMutation,
} from "@/lib/api/apiSlice";
import { getRtkQueryErrorMessage } from "@/lib/error";
import { useRtkInfiniteList } from "@/lib/hooks/useRtkInfiniteList";
import { routes } from "@/lib/constants";
import { toast } from "sonner";
import SellerShopSkeleton, {
  SellerShopProductSkeleton,
} from "@/app/components/common/skeletons/SellerShopSkeleton";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import CustomImage from "@/app/components/common/Image";
import ButtonsHeader from "@/app/components/common/ButtonsHeader";
import { useConfirm } from "@/hooks/use-confirm";
import ConfirmDialog from "@/app/components/common/ConfirmAlert";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const ProductStatusBadge = ({ status }: { status: ProductStatus }) => (
  <span
    className={cn(
      "inline-flex w-fit rounded-full border px-1.5 py-0.5 text-[10px] font-semibold",
      getProductStatusClassName(status),
    )}
  >
    {status}
  </span>
);

const Page = () => {
  const [expandedProductId, setExpandedProductId] = useState<string | null>(
    null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [variantStatusUpdatingId, setVariantStatusUpdatingId] = useState<
    string | null
  >(null);
  const { data: session, status } = useSession();
  const { isOpen, confirm, handleCancel, handleConfirm, title, description } =
    useConfirm();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [updateProduct] = useUpdateProductMutation();
  const [updateProductVariant] = useUpdateProductVariantMutation();

  const sellerId = useMemo(
    () =>
      status === "authenticated" ? session?.user?.sellers?.[0]?.id : undefined,
    [session, status],
  );

  const baseParams = useMemo(
    () => ({
      ...defaultPaginationOptions,
      search: searchTerm,
      sortBy: "isMuminsChoice",
      sortOrder: "DESC",
      sellerId,
    }),
    [searchTerm, sellerId],
  );

  const {
    items: products,
    setItems: setProducts,
    page,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    isLoading,
    loadPage,
    patchItem,
  } = useRtkInfiniteList<IProduct>({
    endpoint: endpoints.product.paginated,
    baseParams,
    skip: !sellerId,
    errorMessage: "Error fetching products",
  });

  const toggleProductExpand = useCallback((product: IProduct) => {
    setExpandedProductId((prev) => (prev === product.id ? null : product.id));
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchInput]);

  useEffect(() => {
    if (!sellerId) {
      return;
    }

    setExpandedProductId(null);
    void loadPage(1, true);
  }, [loadPage, searchTerm, sellerId]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !sellerId) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoading) {
          void loadPage(page + 1);
        }
      },
      {
        threshold: 0.1,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, loadPage, page, sellerId]);

  const handleVariantStockBlur = useCallback(
    async (productId: string, variantIndex: number, rawValue: string) => {
      const nextStock = Number.parseInt(rawValue, 10);
      if (Number.isNaN(nextStock)) {
        toast.error("Please enter a valid stock value");
        return;
      }

      const product = products.find((item) => item.id === productId);
      const variant = product?.variants?.[variantIndex] as
        | (IProduct["variants"][number] & { id?: string })
        | undefined;

      if (!product || !variant) {
        return;
      }

      if (nextStock === (variant.stock ?? 0)) {
        return;
      }

      if (!variant.id) {
        toast.error("Variant id not found. Please refresh and try again.");
        return;
      }

      try {
        await updateProductVariant({
          id: variant.id,
          data: { stock: nextStock },
        }).unwrap();

        toast.success("Variant stock updated successfully");

        setProducts((prev) =>
          prev.map((item) => {
            if (item.id !== productId) {
              return item;
            }

            return {
              ...item,
              variants: (item.variants || []).map((itemVariant, index) => {
                if (index !== variantIndex) {
                  return itemVariant;
                }

                return {
                  ...itemVariant,
                  stock: nextStock,
                };
              }),
            };
          }),
        );
      } catch (error: unknown) {
        toast.error(
          getRtkQueryErrorMessage(error, "Failed to update variant stock"),
        );
      }
    },
    [products, updateProductVariant, setProducts],
  );

  const handleVariantActiveUpdate = useCallback(
    async (
      productId: string,
      variantIndex: number,
      variant: IProductVariant,
      isActive: boolean,
    ) => {
      const variantName =
        variant.variantAttributes
          ?.map((attribute) => attribute.value)
          .filter(Boolean)
          .join(" / ") || `Variant ${variantIndex + 1}`;

      const confirmed = await confirm({
        title: "Change Variant Status",
        description: `Are you sure you want to update "${variantName}"?`,
      });

      if (!confirmed) {
        return;
      }

      if (!variant.id) {
        toast.error("Variant id not found. Please refresh and try again.");
        return;
      }

      setVariantStatusUpdatingId(variant.id);
      try {
        await updateProductVariant({
          id: variant.id,
          data: { isActive },
        }).unwrap();

        setProducts((prev) =>
          prev.map((item) => {
            if (item.id !== productId) {
              return item;
            }

            return {
              ...item,
              variants: (item.variants || []).map((itemVariant, index) => {
                if (index !== variantIndex) {
                  return itemVariant;
                }

                return {
                  ...itemVariant,
                  isActive,
                };
              }),
            };
          }),
        );
        toast.success("Variant status updated successfully");
      } catch (error: unknown) {
        toast.error(
          getRtkQueryErrorMessage(error, "Failed to update variant status"),
        );
      } finally {
        setVariantStatusUpdatingId(null);
      }
    },
    [confirm, setProducts, updateProductVariant],
  );

  const handleProductUpdate = useCallback(
    async (product: IProduct, data: Partial<IProduct>) => {
      const confirmed = await confirm({
        title: "Change Product Status",
        description: `Are you sure you want to update the product "${product.name}"?`,
      });

      if (!confirmed) {
        return;
      }

      setStatusUpdatingId(product.id);
      try {
        await updateProduct({ id: product.id, data }).unwrap();
        patchItem(product.id, data);
        toast.success("Product status updated successfully");
      } catch {
        toast.error("Failed to update product status");
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [confirm, patchItem, updateProduct],
  );

  return (
    <div className={`mx-auto w-full min-h-screen pb-24`}>
      {/* <HomeHeader primaryText="My" secondaryText="Shop" breakLine={false} /> */}
      <ButtonsHeader heading="My Shop" />

      {/* Search */}
      <div className="px-4 pb-4">
        <InputGroup className="w-full rounded-full shadow-sm border-none bg-white">
          <InputGroupInput
            type="text"
            placeholder="Search..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
        </InputGroup>
      </div>
      <div className="px-4">
        {!isInitialLoading && products.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-500">
            No products found.
          </div>
        )}

        {isInitialLoading ? (
          <SellerShopSkeleton />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {products.map((product) => {
              let totalAmount = 0;
              let stock = 0;

              for (const variant of product.variants || []) {
                if (variant.alwaysAvailable) {
                  continue;
                }

                totalAmount += variant.discountedPrice * (variant.stock ?? 0);
                stock += variant.stock ?? 0;
              }

              const isExpanded = expandedProductId === product.id;

              return (
                <div
                  key={product.id}
                  className={`grid grid-cols-4 bg-white rounded-xl ${product.isMuminsChoice ? "border border-primary" : ""}`}
                >
                  <div className="col-span-3">
                    <button
                      type="button"
                      onClick={() => toggleProductExpand(product)}
                      className="flex w-full gap-4 p-4 items-center text-left"
                    >
                      <div className="flex shrink-0 flex-col items-start gap-2">
                        <ProductStatusBadge status={product.status} />
                        <div className="flex h-20 w-20 items-center justify-center rounded-lg">
                          <CustomImage
                            src={product.variants[0].featuredImage}
                            alt={product.name}
                            className="h-20 w-20 rounded-lg object-cover"
                          />
                        </div>
                      </div>

                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-800 leading-snug">
                          {product.name}
                        </h3>

                        {product.isMuminsChoice && (
                          <p className="mt-1 text-[0.6rem] font-semibold text-primary">
                            MUMIN&apos;S CHOICE
                          </p>
                        )}

                        <p className="text-xs text-gray-500 mt-1">
                          Stock - {stock} | Variants -{" "}
                          {product.variants?.length || 0}
                        </p>

                        <div className="flex items-center gap-2 mt-2">
                          <span className="font-semibold text-black">
                            Stock Value: {priceFormat(totalAmount)}
                          </span>
                        </div>

                        <p className="mt-2 text-xs font-medium text-primary">
                          {isExpanded ? "Hide variants" : "View variants"}
                        </p>
                      </div>
                    </button>
                  </div>

                  <div className="flex flex-col gap-2 justify-start p-4">
                    <Link
                      to={routes.products.id(product.id)}
                      aria-label={`View ${product.name}`}
                      className="flex justify-center items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Link>
                    <Link
                      to={routes.seller.myShop.product.id(product.id)}
                      aria-label={`Edit ${product.name}`}
                      className="flex justify-center items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Link>
                    <div className="flex flex-col items-end gap-1">
                      <Switch
                        checked={product.isActive}
                        disabled={statusUpdatingId === product.id}
                        onClick={(event: MouseEvent<HTMLButtonElement>) =>
                          event.stopPropagation()
                        }
                        onCheckedChange={(checked) => {
                          void handleProductUpdate(product, {
                            isActive: checked,
                          });
                        }}
                      />
                      <span className="text-[10px] font-medium text-gray-600">
                        {product.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Input
                        defaultValue={product.location}
                        className="inline w-15 h-6 rounded-md border-gray-300 text-xs text-right"
                        onBlur={(e) => {
                          void handleProductUpdate(product, {
                            location: e.target.value,
                          });
                        }}
                      />
                      <span className="text-[10px] font-medium text-gray-600">
                        Location
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="col-span-4 px-4 pb-4">
                      <div className="border-t border-gray-200 pt-3 space-y-2">
                        {(product.variants || []).length === 0 && (
                          <p className="text-xs text-gray-500">
                            No variants available.
                          </p>
                        )}

                        {(product.variants || []).map((variant, index) => {
                          const attributeValues =
                            variant.variantAttributes
                              ?.map((attribute) => attribute.value)
                              .filter(Boolean) ?? [];
                          const variantName =
                            attributeValues.join(" / ") ||
                            `Variant ${index + 1}`;
                          const availableStock = Math.max(
                            0,
                            (variant.stock ?? 0) -
                              (variant.reservedStock ?? 0),
                          );

                          return (
                            <div
                              key={`${product.id}-${variant.sku || index}`}
                              className={cn(
                                "flex items-start gap-3 rounded-lg bg-[#F7FAFB] px-3 py-2",
                                !variant.isActive && "opacity-80",
                              )}
                            >
                              <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-[#E8F2F4]">
                                <CustomImage
                                  src={variant.featuredImage}
                                  alt={variantName}
                                  className="h-10 w-10 object-cover"
                                />
                              </div>

                              <div className="min-w-0 flex-1">
                                {attributeValues.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {attributeValues.map(
                                      (value, attributeIndex) => (
                                        <span
                                          key={`${variant.sku}-${value}-${attributeIndex}`}
                                          className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-700 ring-1 ring-gray-200"
                                        >
                                          {value}
                                        </span>
                                      ),
                                    )}
                                  </div>
                                ) : (
                                  <p className="truncate text-xs font-medium text-gray-800">
                                    {variantName}
                                  </p>
                                )}

                                <div className="mt-1">
                                  <ProductStatusBadge status={variant.status} />
                                </div>

                                <p className="mt-1 text-[11px] text-gray-500">
                                  SKU: {variant.sku || "-"}
                                </p>
                                <p className="text-[11px] text-gray-500">
                                  Reserved Stock: {variant.reservedStock ?? 0}
                                </p>
                              </div>

                              <div className="shrink-0 text-right">
                                <div className="mb-1 flex flex-col items-end gap-0.5">
                                  <Switch
                                    checked={variant.isActive}
                                    disabled={
                                      variantStatusUpdatingId === variant.id
                                    }
                                    onCheckedChange={(isActive) => {
                                      void handleVariantActiveUpdate(
                                        product.id,
                                        index,
                                        variant,
                                        isActive,
                                      );
                                    }}
                                  />
                                  <span className="text-[10px] font-medium text-gray-600">
                                    {variant.isActive ? "Active" : "Inactive"}
                                  </span>
                                </div>

                                <p className="text-xs font-semibold text-gray-900">
                                  <span>
                                    {priceFormat(variant.discountedPrice)}
                                    &nbsp;
                                  </span>
                                  <span className="text-gray-400 line-through">
                                    {priceFormat(variant.price)}
                                  </span>
                                </p>

                                {variant.alwaysAvailable ? (
                                  <p className="mt-1 text-[11px] font-medium text-green-700">
                                    Always available
                                  </p>
                                ) : (
                                  <p className="text-[11px] text-gray-500">
                                    <span>Stock:</span>&nbsp;
                                    <Input
                                      type="number"
                                      defaultValue={variant.stock}
                                      className="inline w-15 h-6 rounded-md border-gray-300 text-xs text-right"
                                      onBlur={(e) => {
                                        void handleVariantStockBlur(
                                          product.id,
                                          index,
                                          e.target.value,
                                        );
                                      }}
                                    />
                                  </p>
                                )}

                                {!variant.alwaysAvailable &&
                                  (variant.reservedStock ?? 0) > 0 && (
                                    <p className="mt-0.5 text-[10px] text-gray-400">
                                      {availableStock} available
                                    </p>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {isLoadingMore ? (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            <SellerShopProductSkeleton />
            <SellerShopProductSkeleton />
          </div>
        ) : null}

        <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
      </div>

      <Link
        to={routes.seller.myShop.addProduct}
        aria-label="Add product"
        className="fixed bottom-15 right-8 z-20 flex p-2 items-center justify-center rounded-full bg-primary text-white shadow-[0_10px_30px_rgba(15,69,80,0.35)]"
      >
        <Plus size={40} />
      </Link>
      <ConfirmDialog
        isOpen={isOpen}
        title={title}
        description={description}
        confirmButtonLabel="Confirm"
        cancelButtonLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default Page;
